/**
 * Pure helpers for deciding whether the horizontal scroll controls (arrows)
 * should be shown, based on a width rule rather than a hardcoded breakpoint.
 *
 * The row of items fits on one line whenever the viewport is at least the sum
 * of the items' natural widths (no gap) plus the container's horizontal
 * padding. Below that width they overflow and the arrows are shown. Keeping the
 * arithmetic here (rather than inline in a component `$derived`/`$effect`) lets
 * both branches be unit-tested directly.
 */

/**
 * Sum the natural widths of the row's cells and add the container's horizontal
 * padding. Returns `0` when there are no measurable cells (e.g. before layout),
 * which callers treat as "not yet measured" and fall back to a measured overflow
 * signal instead.
 */
export const getContentWidth = (
	cells: readonly HTMLElement[],
	containerPaddingPx: number
): number => {
	const cellsWidth = cells.reduce((sum, cell) => sum + cell.offsetWidth, 0);
	return cellsWidth > 0 ? cellsWidth + containerPaddingPx : 0;
};

/**
 * Decide whether the scroll controls should be visible.
 *
 * Once the content width has been measured (`> 0`), the width rule is the
 * authority: show the controls exactly while the content can't fit on one line
 * at the current viewport width. Until then (`contentWidth === 0`), fall back to
 * the measured overflow signal so nothing flickers on first paint.
 */
export const shouldShowScrollControls = ({
	contentWidth,
	viewportWidth,
	fallback,
}: {
	contentWidth: number;
	viewportWidth: number;
	fallback: boolean;
}): boolean => (contentWidth > 0 ? viewportWidth < contentWidth : fallback);
